import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import BackgroundMedia from "@/components/BackgroundMedia";
import { useBrand } from "@/config/brands";
import {
  ArrowLeft,
  Activity,
  DollarSign,
  Clock,
  AlertTriangle,
  Hospital,
  TrendingUp,
  Shield,
  FileText,
  Heart,
  Brain,
  Bone,
  Eye,
  Baby,
  Stethoscope,
  Building2,
  Code,
  ChevronDown,
  ChevronUp
} from "lucide-react";

interface Procedure {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  body_system?: string;
  international_avg_cost?: number;
  complexity_score?: number;
  risk_category?: string;
  typical_length_of_stay?: number;
  code_type?: string;
  llm_quick_name?: string;
  llm_summary_description?: string;
}

interface HospitalPrice {
  hospital_id: string;
  price: number;
  markup_percentage?: number;
  hospital_name: string;
  hospital_city: string;
  hospital_state: string;
}

interface ChargemasterMapping {
  id: string;
  code: string;
  name: string;
  description?: string;
  hospitalName: string;
  hospitalLocation: string;
  grossCharge: number;
  discountedCash: number;
  inpatientOutpatient?: string;
  grossDiff: number;
  cashDiff: number;
  normalizedUnit?: string;
  hospitalId?: string;
}

interface PricingVariation {
  setting: string;
  dosage_unit: string;
  total_hospitals: number;
  avg_price: number;
  median_price: number;
  min_price: number;
  max_price: number;
  price_spread_percentage: number;
}

interface HospitalRanking {
  hospital_id: string;
  hospital_name: string;
  hospital_city: string;
  hospital_state: string;
  price: number;
  dosage_unit: string;
  setting: string;
}

const ProcedureProfile = () => {
  const { code } = useParams<{ code: string }>();
  const { t } = useTranslation(['procedures', 'codes', 'common']);
  const brand = useBrand();
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [hospitalPrices, setHospitalPrices] = useState<HospitalPrice[]>([]);
  const [chargemasterData, setChargemasterData] = useState<ChargemasterMapping[]>([]);
  const [pricingVariations, setPricingVariations] = useState<PricingVariation[]>([]);
  const [hospitalRankings, setHospitalRankings] = useState<HospitalRanking[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [codeAverage, setCodeAverage] = useState<{avgGross: number, avgCash: number, sampleSize: number} | null>(null);
  const [loading, setLoading] = useState(true);
  const [pricesLoading, setPricesLoading] = useState(true);

  // Helper function to get price color based on markup percentage and brand
  const getPriceColor = (markupPercentage: number | undefined) => {
    if (markupPercentage === undefined) return 'text-foreground';

    if (brand.key === 'antigreed') {
      // Traditional price heatmap: yellow -> orange -> red
      if (markupPercentage < 10) return 'text-yellow-400';
      if (markupPercentage < 50) return 'text-orange-400';
      return 'text-red-500';
    } else {
      // Luigi's: green (good) -> orange -> red (bad)
      if (markupPercentage <= -10) return 'text-green-500';
      if (markupPercentage < 10) return 'text-foreground';
      if (markupPercentage < 50) return 'text-orange-400';
      return 'text-red-500';
    }
  };

  const getPriceBadgeColor = (markupPercentage: number | undefined) => {
    if (markupPercentage === undefined) return 'bg-gray-500/20 text-gray-400';

    if (brand.key === 'antigreed') {
      // Traditional price heatmap: yellow -> orange -> red
      if (markupPercentage < 10) return 'bg-yellow-500/30 text-yellow-400';
      if (markupPercentage < 50) return 'bg-orange-500/30 text-orange-400';
      return 'bg-red-500/30 text-red-400';
    } else {
      // Luigi's: green (good) -> orange -> red (bad)
      if (markupPercentage <= -10) return 'bg-green-500/30 text-green-400';
      if (markupPercentage < 10) return 'bg-gray-500/20 text-gray-400';
      if (markupPercentage < 50) return 'bg-orange-500/30 text-orange-400';
      return 'bg-red-500/30 text-red-400';
    }
  };

  useEffect(() => {
    if (code) {
      fetchProcedure();
      fetchHospitalPrices();
      fetchChargemasterData();
      fetchPricingVariations();
      fetchHospitalRankings();
    }
  }, [code]);

  // Function to calculate average for a specific code
  const calculateCodeAverage = async (searchCode: string) => {
    try {
      const { data, error } = await supabase
        .from('chargemaster_mappings')
        .select('gross_charge, discounted_cash_price')
        .or(`cpt_code.eq.${searchCode},drg_code.eq.${searchCode},icd10_code.eq.${searchCode},chargemaster_code.eq.${searchCode}`)
        .not('gross_charge', 'is', null);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const grossCharges = data.map(d => d.gross_charge).filter(charge => charge !== null);
        const cashPrices = data.map(d => d.discounted_cash_price).filter(price => price !== null);
        
        const avgGross = grossCharges.length > 0 ? grossCharges.reduce((a, b) => a + b, 0) / grossCharges.length : 0;
        const avgCash = cashPrices.length > 0 ? cashPrices.reduce((a, b) => a + b, 0) / cashPrices.length : 0;
        
        return { avgGross, avgCash, sampleSize: data.length };
      }
      
      return { avgGross: 0, avgCash: 0, sampleSize: 0 };
    } catch (error) {
      console.error('Error calculating average:', error);
      return { avgGross: 0, avgCash: 0, sampleSize: 0 };
    }
  };

  const fetchProcedure = async () => {
    try {
      // Decode the URL parameter and remove periods for database search
      const decodedCode = decodeURIComponent(code || '');
      const cleanCode = decodedCode.replace(/\./g, ''); // Remove all periods
      console.log('Original code:', decodedCode, 'Clean code for search:', cleanCode);
      
      const { data, error } = await supabase
        .from('procedures')
        .select('*, llm_quick_name, llm_summary_description')
        .eq('code', cleanCode)
        .maybeSingle();

      if (error) {
        console.error('Error fetching procedure:', error);
        return;
      }

      if (!data) {
        console.log('No procedure found for code:', cleanCode);
      }

      setProcedure(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChargemasterData = async () => {
    try {
      const decodedCode = decodeURIComponent(code || '');
      
      // Calculate average for the code
      const average = await calculateCodeAverage(decodedCode);
      setCodeAverage(average);
      
      // Search chargemaster mappings by code
      const { data, error } = await supabase
        .from('chargemaster_mappings')
        .select(`
          *,
          hospitals!inner(name, city, state)
        `)
        .or(`cpt_code.eq.${decodedCode},drg_code.eq.${decodedCode},icd10_code.eq.${decodedCode},chargemaster_code.eq.${decodedCode}`)
        .limit(20);
      
      if (error) {
        console.error('Error fetching chargemaster data:', error);
        return;
      }

      const mappings = (data || []).map(mapping => {
        const grossCharge = mapping.gross_charge || 0;
        const cashPrice = mapping.discounted_cash_price || 0;
        
        // Calculate percentage difference from average
        let grossDiff = 0;
        let cashDiff = 0;
        if (average && average.avgGross > 0) {
          grossDiff = ((grossCharge - average.avgGross) / average.avgGross) * 100;
        }
        if (average && average.avgCash > 0) {
          cashDiff = ((cashPrice - average.avgCash) / average.avgCash) * 100;
        }

        return {
          id: mapping.id,
          code: mapping.cpt_code || mapping.drg_code || mapping.icd10_code || mapping.chargemaster_code,
          name: mapping.procedure_description || mapping.chargemaster_description,
          description: mapping.procedure_description,
          hospitalName: mapping.hospitals?.name || '',
          hospitalLocation: `${mapping.hospitals?.city}, ${mapping.hospitals?.state}`,
          grossCharge,
          discountedCash: cashPrice,
          inpatientOutpatient: mapping.inpatient_outpatient,
          grossDiff,
          cashDiff
        };
      });

      setChargemasterData(mappings);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHospitalPrices = async () => {
    try {
      // Join chargemaster_mappings with hospitals table to get pricing data
      const { data, error } = await supabase
        .from('chargemaster_mappings')
        .select(`
          gross_charge,
          discounted_cash_price,
          min_negotiated_rate,
          max_negotiated_rate,
          cpt_code,
          drg_code,
          icd10_code,
          hospitals (
            id,
            name,
            city,
            state
          )
        `)
        .or(`cpt_code.eq.${code},drg_code.eq.${code},icd10_code.eq.${code}`)
        .limit(100);
      if (error) {
        console.error('Error fetching hospital prices:', error);
        return;
      }

      // Transform the data to match expected format
      const prices = data?.map(item => ({
        hospital_id: item.hospitals?.id || '',
        price: item.gross_charge || 0,
        markup_percentage: item.gross_charge && item.discounted_cash_price 
          ? ((item.gross_charge - item.discounted_cash_price) / item.discounted_cash_price) * 100 
          : 0,
        hospital_name: item.hospitals?.name || '',
        hospital_city: item.hospitals?.city || '',
        hospital_state: item.hospitals?.state || ''
      })) || [];

      setHospitalPrices(prices);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setPricesLoading(false);
    }
  };

  const fetchPricingVariations = async () => {
    try {
      const decodedCode = decodeURIComponent(code || '');

      // Determine which pricing table to query based on procedure code type
      let pricingTable = 'cpt_pricing_averages';
      let codeColumn = 'cpt_code';
      let avgColumn = 'avg_gross_charge';
      let medianColumn = 'median_gross_charge';
      let minColumn = 'min_gross_charge';
      let maxColumn = 'max_gross_charge';

      // Use the same table determination logic as PriceBreakdownByDosage component
      if (procedure?.code_type) {
        const codeTypeLower = procedure.code_type.toLowerCase();
        if (codeTypeLower === 'cpt') {
          pricingTable = 'cpt_pricing_averages';
          codeColumn = 'cpt_code';
        } else if (codeTypeLower === 'hcpcs') {
          pricingTable = 'hcpcs_pricing_averages';
          codeColumn = 'hcpcs_code';
        } else if (codeTypeLower === 'drg') {
          pricingTable = 'drg_pricing_averages';
          codeColumn = 'drg_code';
        } else if (codeTypeLower === 'ndc' || codeTypeLower === 'drugs') {
          pricingTable = 'drug_pricing_averages';
          codeColumn = 'drug_code';
          avgColumn = 'avg_charge';
          medianColumn = 'median_charge';
          minColumn = 'min_charge';
          maxColumn = 'max_charge';
        } else if (codeTypeLower === 'supply') {
          pricingTable = 'supply_pricing_averages';
          codeColumn = 'supply_code';
          avgColumn = 'avg_charge';
          medianColumn = 'median_charge';
          minColumn = 'min_charge';
          maxColumn = 'max_charge';
        } else if (codeTypeLower === 'rev' || codeTypeLower === 'revenue') {
          pricingTable = 'revenue_pricing_averages';
          codeColumn = 'revenue_code';
          avgColumn = 'avg_charge';
          medianColumn = 'median_charge';
          minColumn = 'min_charge';
          maxColumn = 'max_charge';
        }
      }

      // Query the pre-aggregated pricing table
      const { data, error } = await supabase
        .from(pricingTable)
        .select(`
          setting_type,
          normalized_unit,
          ${avgColumn},
          ${medianColumn},
          ${minColumn},
          ${maxColumn},
          total_hospitals
        `)
        .eq(codeColumn, decodedCode);

      if (error) {
        console.error('Error fetching pricing variations:', error);
        return;
      }

      // Map the data to our interface
      const variations: PricingVariation[] = (data || []).map(row => {
        const min = row[minColumn] || 0;
        const max = row[maxColumn] || 0;
        const spread = min > 0 ? ((max - min) / min) * 100 : 0;

        return {
          setting: row.setting_type || 'Unspecified',
          dosage_unit: row.normalized_unit || 'other',
          total_hospitals: row.total_hospitals || 0,
          avg_price: row[avgColumn] || 0,
          median_price: row[medianColumn] || 0,
          min_price: min,
          max_price: max,
          price_spread_percentage: spread
        };
      });

      setPricingVariations(variations);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchHospitalRankings = async () => {
    try {
      const decodedCode = decodeURIComponent(code || '');

      const { data, error } = await supabase
        .from('chargemaster_mappings')
        .select(`
          hospital_id,
          gross_charge,
          inpatient_outpatient,
          normalized_unit,
          hospitals!inner(name, city, state)
        `)
        .or(`cpt_code.eq.${decodedCode},drg_code.eq.${decodedCode},icd10_code.eq.${decodedCode},chargemaster_code.eq.${decodedCode}`)
        .not('gross_charge', 'is', null)
        .order('gross_charge', { ascending: false });

      if (error) {
        console.error('Error fetching hospital rankings:', error);
        return;
      }

      const rankings: HospitalRanking[] = (data || []).map(row => ({
        hospital_id: row.hospital_id,
        hospital_name: row.hospitals?.name || '',
        hospital_city: row.hospitals?.city || '',
        hospital_state: row.hospitals?.state || '',
        price: row.gross_charge || 0,
        dosage_unit: row.normalized_unit || 'other',
        setting: row.inpatient_outpatient || 'Unspecified'
      }));

      setHospitalRankings(rankings);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const toggleRowExpansion = (setting: string, unit: string) => {
    const key = `${setting}-${unit}`;
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getIconForBodySystem = (bodySystem: string) => {
    switch (bodySystem?.toLowerCase()) {
      case 'cardiovascular':
        return <Heart className="w-5 h-5" />;
      case 'respiratory':
        return <Activity className="w-5 h-5" />;
      case 'musculoskeletal':
        return <Bone className="w-5 h-5" />;
      case 'nervous system':
        return <Brain className="w-5 h-5" />;
      case 'reproductive':
        return <Baby className="w-5 h-5" />;
      case 'visual':
        return <Eye className="w-5 h-5" />;
      default:
        return <Stethoscope className="w-5 h-5" />;
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk?.toLowerCase()) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getComplexityText = (score: number) => {
    if (score >= 7) return t('procedures:profile.complexityVeryHigh');
    if (score >= 5) return t('procedures:profile.complexityHigh');
    if (score >= 3) return t('procedures:profile.complexityMedium');
    return t('procedures:profile.complexityLow');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <BackgroundMedia />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-secondary rounded mb-4 w-1/4"></div>
            <div className="h-12 bg-secondary rounded mb-8 w-3/4"></div>
            <div className="grid md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="terminal-card p-6">
                  <div className="h-4 bg-secondary rounded mb-2"></div>
                  <div className="h-6 bg-secondary rounded"></div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!procedure) {
    return (
      <div className="min-h-screen bg-background relative">
        <BackgroundMedia />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
          <Card className="terminal-card p-12 text-center">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-terminal-green mb-2">{t('procedures:profile.notFound')}</h3>
            <p className="text-muted-foreground mb-4">
              {t('procedures:profile.notFoundDescription', { code })}
            </p>
            <Link to="/search">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('procedures:profile.backToSearch')}
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const averagePrice = hospitalPrices.length > 0 
    ? hospitalPrices.reduce((sum, h) => sum + h.price, 0) / hospitalPrices.length 
    : 0;
  
  const priceRange = hospitalPrices.length > 0 
    ? {
        min: Math.min(...hospitalPrices.map(h => h.price)),
        max: Math.max(...hospitalPrices.map(h => h.price))
      }
    : null;

  const procName = procedure.llm_quick_name || procedure.name || code || '';
  const procDesc = procedure.llm_summary_description
    ? procedure.llm_summary_description.slice(0, 155)
    : `Compare hospital prices for ${procName} (${procedure.code_type?.toUpperCase() || 'CPT'} ${code}) across US hospitals. See average costs, markup percentages, and find the best prices.`;
  // Canonical points at the SITEMAP form `/{code_type}/{code}` (e.g. /cpt/00018,
  // /drg/MS-005) — the legacy `/procedure/{code}` path was never in the sitemap
  // and would canonical-split traffic onto a URL with no crawl signal. Audited
  // 2026-06-14 during the SPA-canonical sweep. Bot SSR returns 404 for
  // `/procedure/{code}` so Helmet is the only canonical source for non-bot
  // visitors arriving via the legacy URL — pointing it at the sitemap form
  // consolidates link-equity correctly.
  const codeTypePath = (() => {
    const ct = (procedure.code_type || 'cpt').toLowerCase();
    if (ct === 'drug' || ct === 'ndc') return 'drugs';
    if (ct === 'revenue' || ct === 'rev') return 'rev';
    return ct;  // cpt, drg, hcpcs unchanged
  })();
  const procCanonical = `${brand.primaryDomain}/${codeTypePath}/${code}`;
  const procTitle = `${procName} Prices — ${procedure.code_type?.toUpperCase() || 'CPT'} ${code} | ${brand.name}`;

  return (
    <div className="min-h-screen bg-background relative">
      <Helmet>
        <title>{procTitle}</title>
        <meta name="description" content={procDesc} />
        <meta name="keywords" content={`${procName}, ${procedure.code_type?.toUpperCase() || 'CPT'} ${code}, hospital prices, medical procedure cost, healthcare transparency`} />
        <link rel="canonical" href={procCanonical} />
        <meta property="og:title" content={procTitle} />
        <meta property="og:description" content={procDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={procCanonical} />
        <meta property="og:image" content={brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={procTitle} />
        <meta name="twitter:description" content={procDesc} />
        <meta name="twitter:image" content={brand.social.ogImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalCode",
            "name": procName,
            "description": procedure.llm_summary_description || procedure.description || procName,
            "codeValue": code,
            "codingSystem": procedure.code_type?.toUpperCase() || "CPT",
            "url": procCanonical
          })}
        </script>
      </Helmet>
      <BackgroundMedia />

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-border bg-gradient-terminal/90 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Link to="/search" className="inline-flex items-center text-terminal-green hover:text-primary mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('procedures:profile.backToSearch')}
            </Link>
            
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  {getIconForBodySystem(procedure.body_system || '')}
                  <Badge variant="outline" className="font-mono">
                    {procedure.code}
                  </Badge>
                  <Badge variant="secondary">
                    {procedure.code_type || 'ICD10'}
                  </Badge>
                  {procedure.risk_category && (
                    <Badge variant={getRiskColor(procedure.risk_category)}>
                      {t('procedures:profile.risk', { level: procedure.risk_category })}
                    </Badge>
                  )}
                </div>
                
                <h1 className="text-3xl font-bold text-terminal-green mb-2 glitch" data-text={procedure.name}>
                  {procedure.name}
                </h1>

                {procedure.description && (
                  <p className="text-lg text-muted-foreground">
                    {procedure.description}
                  </p>
                )}

                {procedure.llm_quick_name && (
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <h2 className="text-xl font-bold text-blue-400 mb-2">
                      {procedure.llm_quick_name}
                    </h2>
                    {procedure.llm_summary_description && (
                      <p className="text-sm text-muted-foreground">
                        {procedure.llm_summary_description}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-6 py-12">
          {/* Procedure Overview */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="terminal-card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <FileText className="w-5 h-5 text-terminal-green" />
                <h3 className="font-semibold">{t('procedures:profile.codeInformation')}</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.code')}</span>
                  <span className="font-mono">{procedure.code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.type')}</span>
                  <span>{procedure.code_type || t('procedures:profile.defaultCodeType')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.category')}</span>
                  <span>{procedure.category || t('procedures:profile.notAvailable')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.bodySystem')}</span>
                  <span>{procedure.body_system || t('procedures:profile.general')}</span>
                </div>
              </div>
            </Card>

            <Card className="terminal-card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Activity className="w-5 h-5 text-terminal-green" />
                <h3 className="font-semibold">{t('procedures:profile.complexityAndRisk')}</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.complexity')}</span>
                  <span>{getComplexityText(procedure.complexity_score || 1)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('procedures:profile.riskLevel')}</span>
                  <span>{procedure.risk_category || t('procedures:profile.complexityLow')}</span>
                </div>
                {procedure.typical_length_of_stay && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('procedures:profile.typicalStay')}</span>
                    <span>{t('procedures:profile.days', { count: procedure.typical_length_of_stay })}</span>
                  </div>
                )}
              </div>
            </Card>

            <Card className="terminal-card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <DollarSign className="w-5 h-5 text-terminal-green" />
                <h3 className="font-semibold">{t('procedures:profile.costInformation')}</h3>
              </div>
              <div className="space-y-2 text-sm">
                {procedure.international_avg_cost && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('procedures:profile.internationalAvg')}</span>
                    <span className="font-semibold text-terminal-green">
                      ${procedure.international_avg_cost.toLocaleString()}
                    </span>
                  </div>
                )}
                {averagePrice > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('procedures:profile.usHospitalAvg')}</span>
                    <span className="font-semibold">
                      ${averagePrice.toLocaleString()}
                    </span>
                  </div>
                )}
                {priceRange && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('procedures:profile.priceRange')}</span>
                    <span>${priceRange.min.toLocaleString()} - ${priceRange.max.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Hospital Pricing from Chargemaster Data */}
          {chargemasterData.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-terminal-green">{t('procedures:profile.hospitalPricingData')}</h2>
                {codeAverage && codeAverage.sampleSize > 0 && (
                  <div className="text-sm bg-secondary/50 rounded-lg p-3 font-mono">
                    <div className="font-bold text-primary mb-1">{t('procedures:profile.marketAverages')}</div>
                    <div>{t('procedures:profile.gross')} ${codeAverage.avgGross.toLocaleString()}</div>
                    <div>{t('procedures:profile.cash')} ${codeAverage.avgCash.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t('procedures:profile.basedOnRecords', { count: codeAverage.sampleSize })}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="grid gap-3">
                {chargemasterData.map((result, index) => (
                  <Card key={result.id} className={`terminal-card p-4 border-l-4 ${
                    index % 2 === 0 ? 'border-l-blue-500 bg-blue-500/5' : 'border-l-purple-500 bg-purple-500/5'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Code className="w-4 h-4 text-primary mr-2" />
                          <span className="font-mono text-sm font-bold text-primary">
                            {result.code}
                          </span>
                          <Building2 className="w-4 h-4 ml-4 mr-1 text-muted-foreground" />
                          <span className="font-semibold text-sm">{result.hospitalName}</span>
                        </div>
                        
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">{result.hospitalLocation}</p>
                            <p className="text-xs mt-1">
                              <span className="font-semibold">{t('procedures:profile.setting')}</span> {result.inpatientOutpatient || t('procedures:profile.notAvailable')}
                            </p>
                          </div>
                          
                          <div className="space-y-1">
                            {result.grossCharge > 0 && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-semibold">{t('procedures:profile.gross')}</span>
                                <span className={`font-mono font-bold text-lg ${getPriceColor(result.grossDiff)}`}>
                                  ${result.grossCharge.toLocaleString()}
                                </span>
                                {result.grossDiff !== 0 && (
                                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${getPriceBadgeColor(result.grossDiff)}`}>
                                    {result.grossDiff > 0 ? '+' : ''}{result.grossDiff.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            )}
                            {result.discountedCash > 0 && (
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-semibold">{t('procedures:profile.cash')}</span>
                                <span className={`font-mono font-bold text-lg ${getPriceColor(result.cashDiff)}`}>
                                  ${result.discountedCash.toLocaleString()}
                                </span>
                                {result.cashDiff !== 0 && (
                                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${getPriceBadgeColor(result.cashDiff)}`}>
                                    {result.cashDiff > 0 ? '+' : ''}{result.cashDiff.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="text-xs">
                            <h4 className="font-semibold mb-1 text-foreground">{result.name}</h4>
                            {result.description && (
                              <p className="text-muted-foreground line-clamp-2">{result.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Hospital Price Rankings by Care Setting */}
          {pricingVariations.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-terminal-green mb-6">{t('procedures:profile.hospitalPriceRankings')}</h2>

              <Tabs defaultValue="all" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="all">{t('procedures:profile.allSettings')}</TabsTrigger>
                  <TabsTrigger value="Inpatient">{t('procedures:profile.inpatient')}</TabsTrigger>
                  <TabsTrigger value="Outpatient">{t('procedures:profile.outpatient')}</TabsTrigger>
                  <TabsTrigger value="Unspecified">{t('procedures:profile.unspecified')}</TabsTrigger>
                </TabsList>

                {['all', 'Inpatient', 'Outpatient', 'Unspecified'].map(settingFilter => {
                  const filteredVariations = settingFilter === 'all'
                    ? pricingVariations
                    : pricingVariations.filter(v => v.setting === settingFilter);

                  // Sort by setting first (Inpatient -> Outpatient -> Unspecified),
                  // then by total_hospitals descending within each setting
                  const settingOrder = { 'Inpatient': 1, 'Outpatient': 2, 'Unspecified': 3 };
                  const sortedVariations = [...filteredVariations].sort((a, b) => {
                    // If showing all settings, group by setting first
                    if (settingFilter === 'all') {
                      const settingCompare = (settingOrder[a.setting as keyof typeof settingOrder] || 999) -
                                             (settingOrder[b.setting as keyof typeof settingOrder] || 999);
                      if (settingCompare !== 0) return settingCompare;
                    }
                    // Within each setting, sort by hospital count descending
                    return b.total_hospitals - a.total_hospitals;
                  });

                  return (
                    <TabsContent key={settingFilter} value={settingFilter} className="space-y-4">
                      {sortedVariations.length === 0 ? (
                        <Card className="terminal-card p-8 text-center">
                          <p className="text-muted-foreground">{t('procedures:profile.noPricingData', { setting: settingFilter })}</p>
                        </Card>
                      ) : (
                        <div className="space-y-2">
                          {sortedVariations.map((variation, idx) => {
                            const rowKey = `${variation.setting}-${variation.dosage_unit}`;
                            const isExpanded = expandedRows.has(rowKey);
                            const filteredHospitals = hospitalRankings.filter(
                              h => h.setting === variation.setting && h.dosage_unit === variation.dosage_unit
                            ).sort((a, b) => b.price - a.price);

                            // Show section header when setting changes (only for "all" tab)
                            const prevVariation = idx > 0 ? sortedVariations[idx - 1] : null;
                            const showSettingHeader = settingFilter === 'all' &&
                                                     (!prevVariation || prevVariation.setting !== variation.setting);

                            return (
                              <React.Fragment key={rowKey}>
                                {showSettingHeader && (
                                  <div className="pt-4 pb-2">
                                    <h3 className="text-lg font-bold text-terminal-green flex items-center">
                                      {variation.setting === 'Inpatient' && `🏥 ${t('procedures:profile.inpatientCareSettings')}`}
                                      {variation.setting === 'Outpatient' && `🚶 ${t('procedures:profile.outpatientCareSettings')}`}
                                      {variation.setting === 'Unspecified' && `❓ ${t('procedures:profile.unspecifiedCareSettings')}`}
                                    </h3>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {variation.setting === 'Inpatient' && t('procedures:profile.inpatientDescription')}
                                      {variation.setting === 'Outpatient' && t('procedures:profile.outpatientDescription')}
                                      {variation.setting === 'Unspecified' && t('procedures:profile.unspecifiedDescription')}
                                    </p>
                                  </div>
                                )}
                                <Card className="terminal-card overflow-hidden">
                                {/* Main Row - Pricing Summary */}
                                <div
                                  className="p-4 cursor-pointer hover:bg-secondary/20 transition-colors"
                                  onClick={() => toggleRowExpansion(variation.setting, variation.dosage_unit)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4 flex-1">
                                      <div className="flex items-center">
                                        {isExpanded ? (
                                          <ChevronUp className="w-5 h-5 text-terminal-green" />
                                        ) : (
                                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                                        )}
                                      </div>

                                      <div>
                                        <div className="flex items-center space-x-2">
                                          <Badge variant="outline" className="font-mono">
                                            {variation.dosage_unit}
                                          </Badge>
                                          {settingFilter === 'all' && (
                                            <Badge variant="secondary" className="text-xs">
                                              {variation.setting}
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                          {t('procedures:profile.hospitalCount', { count: variation.total_hospitals })}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-4 gap-6 text-sm">
                                      <div className="text-center">
                                        <p className="text-xs text-muted-foreground mb-1">{t('procedures:profile.average')}</p>
                                        <p className="font-mono font-bold text-terminal-green">
                                          ${variation.avg_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-xs text-muted-foreground mb-1">{t('procedures:profile.median')}</p>
                                        <p className="font-mono font-semibold">
                                          ${variation.median_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-xs text-muted-foreground mb-1">{t('procedures:profile.range')}</p>
                                        <p className="font-mono text-xs">
                                          ${variation.min_price.toLocaleString(undefined, { maximumFractionDigits: 0 })} - ${variation.max_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                        </p>
                                      </div>
                                      <div className="text-center">
                                        <p className="text-xs text-muted-foreground mb-1">{t('procedures:profile.priceSpread')}</p>
                                        <p className="font-mono font-semibold text-orange-400">
                                          {variation.price_spread_percentage.toFixed(0)}%
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded Section - Individual Hospital Rankings */}
                                {isExpanded && filteredHospitals.length > 0 && (
                                  <div className="border-t border-border bg-secondary/10">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="w-16">{t('procedures:profile.rank')}</TableHead>
                                          <TableHead>{t('procedures:profile.hospitalName')}</TableHead>
                                          <TableHead>{t('procedures:profile.location')}</TableHead>
                                          <TableHead className="text-right">{t('procedures:profile.price')}</TableHead>
                                          <TableHead className="text-right">{t('procedures:profile.vsAvg')}</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {filteredHospitals.map((hospital, index) => {
                                          const vsAvg = ((hospital.price - variation.avg_price) / variation.avg_price) * 100;
                                          return (
                                            <TableRow key={`${hospital.hospital_id}-${index}`}>
                                              <TableCell className="font-mono text-xs">
                                                #{index + 1}
                                              </TableCell>
                                              <TableCell className="font-semibold">
                                                {hospital.hospital_name}
                                              </TableCell>
                                              <TableCell className="text-sm text-muted-foreground">
                                                {hospital.hospital_city}, {hospital.hospital_state}
                                              </TableCell>
                                              <TableCell className="text-right font-mono font-bold text-lg">
                                                ${hospital.price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                              </TableCell>
                                              <TableCell className="text-right">
                                                <span className={`text-sm font-bold px-2 py-1 rounded ${getPriceBadgeColor(vsAvg)}`}>
                                                  {vsAvg > 0 ? '+' : ''}{vsAvg.toFixed(1)}%
                                                </span>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </Card>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          )}

          {/* Price Corruption Alert - Only show if there are hospital prices */}
          {hospitalPrices.length > 0 && (
          <Card className="terminal-card p-8 corruption mb-12">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-blood-red mb-4 glitch" data-text={t('procedures:profile.priceManipulationDetected')}>
                {t('procedures:profile.priceManipulationDetected')}
              </h2>
              <p className="text-foreground mb-6 font-mono">
                {t('procedures:profile.priceManipulationDescription', {
                  multiplier: priceRange && procedure.international_avg_cost
                    ? Math.round(priceRange.max / procedure.international_avg_cost)
                    : '10'
                })}
              </p>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center justify-center p-4 bg-secondary rounded">
                  <AlertTriangle className="w-5 h-5 text-blood-red mr-2" />
                  <span>{t('procedures:profile.priceGougingConfirmed')}</span>
                </div>
                <div className="flex items-center justify-center p-4 bg-secondary rounded">
                  <TrendingUp className="w-5 h-5 text-blood-red mr-2" />
                  <span>{t('procedures:profile.systematicExploitation')}</span>
                </div>
                <div className="flex items-center justify-center p-4 bg-secondary rounded">
                  <Shield className="w-5 h-5 text-blood-red mr-2" />
                  <span>{t('procedures:profile.patientRightsViolated')}</span>
                </div>
              </div>
            </div>
          </Card>
          )}
        </main>
      </div>
    </div>
  );
};

export default ProcedureProfile;